import React from 'react'
import styled, { keyframes, css } from 'styled-components'
import { colors } from '@src/theme/sharedComponents'

export type BannerVariant = 'primary' | 'accent' | 'warning'

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-100%); }
  to { opacity: 1; transform: translateY(0); }
`

const slideUp = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to { opacity: 0; transform: translateY(-100%); }
`

const BannerWrapper = styled.div<{ $variant: BannerVariant; $isClosing?: boolean }>`
  position: relative;
  width: 100%;
  padding: 0.75rem 3rem 0.75rem 1.5rem;
  font-weight: 700;
  font-size: 0.85rem;
  color: white;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  z-index: 1010;

  background: ${p => {
    switch (p.$variant) {
      case 'accent':  return colors.accent
      case 'warning': return colors.danger
      default:        return colors.primary
    }
  }};

  border-left: 4px solid rgba(255, 255, 255, 0.4);

  animation: ${p => p.$isClosing
    ? css`${slideUp} 0.2s ease-out forwards`
    : css`${slideDown} 0.3s ease-out`
  };
`

const BannerMessage = styled.span`
  i { margin-right: 0.5rem; }
  a { color: inherit; text-decoration: underline; font-weight: 800; }
  strong { font-weight: 900; }
`

const CloseButton = styled.button`
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: inherit;
  font-size: 1.1rem;
  opacity: 0.7;
  transition: opacity 0.15s;
  &:hover { opacity: 1; }
  &::before { content: '×'; font-weight: 300; }
`

const ICONS: Record<BannerVariant, string> = {
  primary: 'bi-info-circle-fill',
  accent:  'bi-info-circle-fill',
  warning: 'bi-exclamation-triangle-fill',
}

interface NotificationBannerProps {
  message: string | React.ReactNode
  variant?: BannerVariant
  onClose?: () => void
}

const NotificationBanner: React.FC<NotificationBannerProps> = ({ message, variant = 'primary', onClose }) => {
  const [isClosing, setIsClosing] = React.useState(false)

  const handleClose = React.useCallback(() => {
    setIsClosing(true)
    setTimeout(() => onClose?.(), 200)
  }, [onClose])

  return (
    <BannerWrapper $variant={variant} $isClosing={isClosing} role="alert">
      <BannerMessage>
        <i className={ICONS[variant]} aria-hidden="true" />
        {message}
      </BannerMessage>
      {onClose && <CloseButton onClick={handleClose} aria-label="Close notification" type="button" />}
    </BannerWrapper>
  )
}

export default NotificationBanner
